<template>
  <div :class="['x-icon-asset', `x-icon-asset--${props.type}`, 'x-icon-asset--warning']">
    <slot>
      <Tooltip
        v-if="props.isTooltip"
        trigger="hover"
        :placement="tooltipPlacement"
        theme="dark"
        class="x-icon-tooltip"
        max-width="auto"
        content-class="x-icon-asset__tooltip-wrapper"
      >
        <template #content>
          <div class="x-icon-asset__tooltip-content" @click.stop>
            <div class="x-icon-asset__content-container">
              <Text class="x-icon-asset__content-text">
                <slot v-if="$slots.text" name="text" />
                <template v-else>{{ text }}</template>
              </Text>
              <Button
                variant="purple"
                size="compact"
                class="x-icon-asset__upgrade-button"
                @mousedown.stop.prevent="emits('upgrade')"
              >
                {{ btnText }}
              </Button>
            </div>
          </div>
        </template>
        <slot name="trigger-icon">
          <div class="x-icon-asset__trigger" @click="emits('click')">
            <div
              class="x-icon-asset__icon-container"
              :class="{ 'x-icon-asset__icon-container--icon': props.type === 'icon' }"
            >
              <IconCrown class="x-icon-asset__icon" />
            </div>
            <span
              v-if="props.type === 'num'"
              class="x-icon-asset__count"
              :class="{
                'x-icon-asset__count--disabled': props.current === 0,
              }"
            >
              {{ props.current }}/{{ props.total }}
            </span>
          </div>
        </slot>
      </Tooltip>
      <div
        class="x-icon-asset__icon-container"
        :class="{ 'x-icon-asset__icon-container--icon': props.type === 'icon' }"
        v-else
      >
        <IconCrown class="x-icon-asset__icon" />
      </div>
    </slot>
  </div>
</template>
<script setup lang="ts">
import IconCrown from '@rendora/design/assets/color/crown.svg?component';

import { computed } from 'vue';
import { Button } from '../../components/button/index';
import { Text } from '../../components/text/index';
import { Tooltip } from '../../components/tooltip/index';
import type { TooltipPlacement } from '../../components/tooltip/types';

export type IconAssetType = 'icon' | 'num';

const props = withDefaults(
  defineProps<{
    type?: IconAssetType;
    isTooltip?: boolean;
    total?: number;
    current?: number;
    position?:
      | 'bottomCenter'
      | 'bottomLeft'
      | 'bottomRight'
      | 'right'
      | 'topCenter'
      | 'topLeft'
      | 'topRight';
    noAsset?: boolean; // 有无资产
    btnText?: string; // 按钮文字
    text?: string; // 提示文字
  }>(),
  {
    isTooltip: true,
    total: 0,
    current: 0,
    position: 'topCenter',
    btnText: '升级权益',
  },
);

defineSlots<{
  default?: (props: Record<string, never>) => any;
  text?: (props: Record<string, never>) => any;
  'trigger-icon'?: (props: Record<string, never>) => any;
}>();

const emits = defineEmits(['upgrade', 'click']);

// position 到 tooltip placement 的映射
const POSITION_MAP: Record<NonNullable<typeof props.position>, TooltipPlacement> = {
  topCenter: 'top',
  topLeft: 'topLeft',
  topRight: 'topRight',
  bottomCenter: 'bottom',
  bottomLeft: 'bottomLeft',
  bottomRight: 'bottomRight',
  right: 'right',
};

const tooltipPlacement = computed<TooltipPlacement>(() => {
  return POSITION_MAP[props.position] || 'top';
});
</script>
<style lang="scss">
.x-icon-asset__tooltip-wrapper {
  .x-icon-asset__content-text {
    flex-shrink: 0;
  }
}
</style>
<style lang="scss" scoped>
.x-icon-asset__icon-container {
  padding: 0;
}

.x-icon-asset__icon-container--icon {
  padding: 1px 4px;
  border-radius: 9px;
  background-color: var(--x-black-5);
}

.x-icon-asset__icon {
  font-size: 14px !important;
}

.x-icon-asset {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 9px;
  cursor: pointer;
}

.x-icon-asset--num {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 6px;
  height: 18px;
  border-radius: 11px;
  background: var(--x-back-5);

  &.x-icon-asset--warning {
    color: var(--x-warning-7);
    background: var(--x-warning-2);
  }
}

.x-icon-asset--icon .x-icon-asset__icon {
  color: var(--x-warning-5);
}

.x-icon-asset--num .x-icon-asset__icon {
  color: var(--x-warning-7);
}

.x-icon-asset__trigger {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
}

.x-icon-asset__count {
  color: var(--x-warning-9);
  font-weight: var(--x-weight-medium);
  font-size: var(--x-text-12-size);
  line-height: var(--x-text-12-line-height);
}

.x-icon-asset__upgrade-button {
  flex-shrink: 0;
}

:deep(.x-icon-asset__tooltip-wrapper) {
  padding: 0;
  background: transparent;
}

.x-icon-asset__tooltip-content {
  background: var(--x-black-1);
  border-radius: 8px;
  z-index: 999;
  color: var(--x-white-1);
}

.x-icon-asset__content-container {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.x-icon-asset__content-text {
  color: var(--x-white-1);
}
</style>
